/**
 * ranking.js — apresentação do menu "Rankings" (novo modelo de reconhecimento).
 *
 * Portado de biteams_app_vue/src/utils/ranking.js, só com o que o app do consultor usa. Aqui NÃO
 * existe cálculo: pontos, ordenação e posição vêm prontos da API (salesteams_app_api,
 * controllers/Rankings.php + models/rankings/Model_rankings.php). Os pesos abaixo servem só para
 * rotular as colunas ("×2") — se a regra mudar, o número que vale é o do backend, e estes rótulos
 * precisam ser atualizados junto.
 */

export const INDICADORES = {
  producao: { label: "Produção", curto: "Produção" },
  producaoDiretos: { label: "Produção dos diretos", curto: "Prod. diretos" },
  consultorAtivo: { label: "Consultores ativos", curto: "Ativos" },
  formacaoLider: { label: "Formação de Consultor Líder", curto: "Form. líder" },
  formacaoRegional: { label: "Formação de Regional", curto: "Form. regional" },
};

const PESOS_CONSULTOR = [
  { key: "producao", peso: 1 },
  { key: "consultorAtivo", peso: 2 },
  { key: "formacaoLider", peso: 5 },
];

const PESOS_GERENTE = [
  { key: "producao", peso: 1 },
  { key: "producaoDiretos", peso: 0.5 },
  { key: "consultorAtivo", peso: 2 },
  { key: "formacaoLider", peso: 5 },
  { key: "formacaoRegional", peso: 15 },
];

/**
 * Uma entrada por trilha. `path` é a rota do app (e a permissão da secretária); `endpoint` é a
 * URI da API — iguais de propósito, ver config/routes.php do salesteams_app_api.
 */
export const TRILHAS = {
  consultores: {
    id: "consultores",
    path: "/biteams/rk-consultores",
    endpoint: "biteams/rk-consultores",
    menuLabel: "Consultores",
    titulo: "TOP 20 Consultores",
    subtitulo: "Ranking mensal de consultores por pontuação.",
    entidadeLabel: "Consultor",
    pesos: PESOS_CONSULTOR,
    trimestral: false,
    periodicidade: "Mensal",
    premiacao: "R$ 5 mil para os 5 primeiros",
  },
  home: {
    id: "home",
    path: "/biteams/rk-home",
    endpoint: "biteams/rk-home",
    menuLabel: "Home Office",
    titulo: "TOP 20 Regionais Home Office",
    subtitulo: "Ranking mensal das regionais home office por pontuação.",
    entidadeLabel: "Regional",
    pesos: PESOS_CONSULTOR,
    trimestral: false,
    periodicidade: "Mensal",
    premiacao: "R$ 5.000,00",
  },
  executiva: {
    id: "executiva",
    path: "/biteams/rk-executiva",
    endpoint: "biteams/rk-executiva",
    menuLabel: "Executiva",
    titulo: "TOP 20 Regionais Executivo e acima",
    subtitulo: "Mesmo regulamento do Home Office; muda apenas a hierarquia apurada.",
    entidadeLabel: "Regional",
    pesos: PESOS_CONSULTOR,
    trimestral: false,
    periodicidade: "Mensal",
    premiacao: "R$ 5.000,00",
  },
  gerentes: {
    id: "gerentes",
    path: "/biteams/rk-gerentes",
    endpoint: "biteams/rk-gerentes",
    menuLabel: "Gerentes",
    titulo: "TOP 20 Gerentes",
    subtitulo: "Ranking trimestral do núcleo de gerentes.",
    entidadeLabel: "Gerente",
    pesos: PESOS_GERENTE,
    trimestral: true,
    periodicidade: "Trimestral",
    premiacao: "Deslocamento para o encontro trimestral + reunião estratégica",
  },
};

// É uma empresa OU a outra — sem opção somada (decisão do usuário, 2026-09-17).
export const EMPRESAS = [
  { value: "brasil", text: "Brasil" },
  { value: "truck", text: "Truck" },
];

export const MESES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

export const MEDALHAS = { 1: "🥇", 2: "🥈", 3: "🥉" };

/** Pontos: inteiros sem casas, meios pontos com uma casa (peso 0,5 da produção dos diretos). */
export function fmtPontos(v) {
  if (v === null || v === undefined || isNaN(v)) return "—";
  const n = Number(v);
  return Number.isInteger(n)
    ? n.toLocaleString("pt-BR")
    : n.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

export function fmtPeso(peso) {
  return String(peso).replace(".", ",");
}

/** "Setembro/2026" ou "3º trimestre/2026". */
export function fmtPeriodo(periodo) {
  if (!periodo) return "";
  if (periodo.tipo === "trimestre") return `${periodo.trimestre}º trimestre/${periodo.ano}`;
  return `${MESES[periodo.mes - 1] || ""}/${periodo.ano}`;
}

/**
 * "YYYY-MM-DD HH:MM:SS" (horário de Brasília, gravado pelo PHP) → "17/09 às 14:05". Montado na mão
 * porque o Date do Safari não aceita esse formato com espaço, e converter pelo fuso do aparelho
 * mudaria a hora.
 */
export function fmtAtualizadoEm(valor) {
  if (!valor) return "";
  const [data, hora] = String(valor).split(" ");
  const [, m, d] = (data || "").split("-");
  if (!d || !m) return "";
  return hora ? `${d}/${m} às ${hora.slice(0, 5)}` : `${d}/${m}`;
}

/**
 * Iniciais para o avatar. Ignora conectivos e o sufixo entre parênteses dos nomes de regional
 * ("REGIONAL BARRA DA TIJUCA (ADSON)" → "BT").
 */
export function iniciais(nome) {
  if (!nome) return "?";
  const limpo = String(nome).replace(/\(.*?\)/g, " ").replace(/[^A-Za-zÀ-ÿ\s]/g, " ");
  const partes = limpo
    .split(/\s+/)
    .filter((p) => p.length > 2 && ["DA", "DE", "DO", "DAS", "DOS", "REGIONAL"].indexOf(p.toUpperCase()) === -1);
  if (!partes.length) return String(nome).trim().slice(0, 2).toUpperCase();
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[1][0]).toUpperCase();
}
